
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageContainer } from '@/components/layout/PageContainer';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BookingsTabs } from '@/components/bookings/BookingsTabs';
import { BookingsList } from '@/components/bookings/BookingsList';
import { formatDate, formatCurrency } from '@/utils/formatters';
import type { Tables } from '@/integrations/supabase/types';

// This function could be moved to a `services/bookingService.ts` file
async function fetchAllBookings() {
  const { data: bookings, error } = await supabase
    .from('booking_details')
    .select('*')
    .order('start_date', { ascending: true });
  
  if (error) throw error;
  return bookings || [];
}

const useFetchBookings = () => {
  return useQuery({
    queryKey: ['bookings'],
    queryFn: fetchAllBookings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

const BookingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('upcoming');

  // Fetch bookings using React Query pattern (currently mocked)
  const { data: allBookings = [], isLoading: loading, error } = useFetchBookings();

  useEffect(() => {
    if (error) {
      toast.error('Error fetching bookings', {
        description: error.message || 'Please try again later'
      });
    }
  }, [error]);

  const { upcomingBookings, pastBookings } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcoming: Tables<'booking_details'>[] = [];
    const past: Tables<'booking_details'>[] = [];

    for (const booking of allBookings) {
      const dateToCompare = booking.end_date 
        ? new Date(booking.end_date) 
        : (booking.start_date ? new Date(booking.start_date) : null);
      
      if (dateToCompare) {
        if (dateToCompare >= today) {
          upcoming.push(booking);
        } else {
          past.push(booking);
        }
      }
    }
    
    // Sort past bookings in descending order (most recent first)
    past.sort((a, b) => new Date(b.start_date ?? 0).getTime() - new Date(a.start_date ?? 0).getTime());

    return { upcomingBookings: upcoming, pastBookings: past };
  }, [allBookings]);

  return (
    <PageContainer title="Bookings" subtitle="Manage your bookings">
      <div className="space-y-4">
        <BookingsTabs activeTab={activeTab} setActiveTab={setActiveTab} />

        <div className="mt-0 space-y-3 pb-20">
          {activeTab === 'upcoming' && (
            <BookingsList
              bookings={upcomingBookings}
              loading={loading}
              isUpcoming={true}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
            />
          )}
          
          {activeTab === 'past' && (
            <BookingsList
              bookings={pastBookings}
              loading={loading}
              isUpcoming={false}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
            />
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default BookingsPage;
